import { useQuery } from '@tanstack/react-query';
import { doorAccessCountKey } from '../lib/queryKeys';

export interface UseDoorAccessCounts {
  enabledCount: number | undefined;
  disabledCount: number | undefined;
  isPending: boolean;
  hasEnabled: boolean;
  isError: boolean;
  // The enabled read's rejection, the only one that is the card's error, so the host can tell
  // whether it has already answered it with a screen of its own.
  error: unknown;
  retry: () => void;
}

// The two sides of the gate, read as numbers so the cache never holds the rows.
//
// The two reads are deliberately NOT symmetric. Without the enabled count there is no number to put
// on the card, so that failure is the card's error. Without the disabled count there still is one —
// only the denominator, the share and the bar are missing — so that failure is a partial state the
// card renders around, not an error that blanks it.
//
// Errors stay inline (default `throwOnError: false`): a failed count must surface a retry on this
// card alone, never tear down the cards beside it through the page boundary.
export function useDoorAccessCounts(
  fetchCount: (enabled: boolean) => Promise<number>,
): UseDoorAccessCounts {
  const enabled = useQuery({
    queryKey: doorAccessCountKey(true),
    queryFn: () => fetchCount(true),
  });
  const disabled = useQuery({
    queryKey: doorAccessCountKey(false),
    queryFn: () => fetchCount(false),
  });

  return {
    enabledCount: enabled.data,
    disabledCount: disabled.data,
    isPending: enabled.isPending || disabled.isPending,
    hasEnabled: enabled.isSuccess,
    isError: enabled.isError,
    error: enabled.error,
    retry: () => {
      void enabled.refetch();
      void disabled.refetch();
    },
  };
}
